import { ApolloProvider } from "@apollo/client";
import { AuthProvider, getAccessToken, getLoginRedirectUrl, setAuthData } from "@qw/auth";
import { createApolloClient } from "@qw/graphql";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RootErrorBoundary } from "./app/root-error-boundary";
import { App } from "./app.tsx";
import { captureAuthFailure } from "./auth-error-telemetry";
import { LAUNCH_DARKLY_CLIENT_ID } from "./constants/env";
import { LaunchDarklyIdentify } from "./launch-darkly-identify";
import { initPostHog, resetPostHog } from "./posthog";
import { clearQwibTranscriptDimensions } from "./qwib-telemetry";

const apiHost = import.meta.env.VITE_REACT_APP_API_HOST || "localhost:4000";
const httpProtocol = apiHost.startsWith("localhost") ? "http" : "https";
const wsProtocol = apiHost.startsWith("localhost") ? "ws" : "wss";

const apiUrl = `${httpProtocol}://${apiHost}/graphql`;
const wsUrl = `${wsProtocol}://${apiHost}/graphql`;
const authClientId = import.meta.env.VITE_REACT_APP_AUTH_CLIENT_ID as string;

initPostHog();

// Transcript dimensions are super properties, which posthog-js persists across a reload — including
// the reload out of a crash. Clear them before the tree mounts so nothing is attributed to a
// transcript that is no longer on screen.
clearQwibTranscriptDimensions();

const apolloClient = createApolloClient({
  httpUri: apiUrl,
  wsUri: wsUrl,
  getAccessToken,
  // Only reached for a 401 that belongs to the credential currently in storage — the link ignores
  // one raised by a request that carried an older token, or none, since a newer one was stored.
  // Without that, a 401 in flight when a login landed would wipe the token that was just minted
  // and the navigation below would cancel every request behind it (B-1963).
  //
  // A full document navigation, not `navigate()`: this handler is module scope, outside the router,
  // and `setAuthData` is the imperative `localStorage` writer — the `storage` event is not
  // delivered to the tab that caused it, so `authDataAtom` keeps its in-memory value in this tab
  // and `AuthGuard` would never see the sign-out. The reload is what makes it take effect.
  onUnauthorized: () => {
    resetPostHog();
    setAuthData(null);
    window.location.href = getLoginRedirectUrl();
  },
});

const AppWithLaunchDarklyIdentify = () => (
  <LaunchDarklyIdentify>
    <App />
  </LaunchDarklyIdentify>
);

const AppWithLD = withLDProvider({
  clientSideID: LAUNCH_DARKLY_CLIENT_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(AppWithLaunchDarklyIdentify);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* Outermost, above every provider: a fatal render must leave a Reload button, not an empty
        `#root` painted in the page background (B-2012/B-2020). */}
    <RootErrorBoundary>
      <AuthProvider authClientId={authClientId} onAuthFailure={captureAuthFailure}>
        <ApolloProvider client={apolloClient}>
          <AppWithLD />
        </ApolloProvider>
      </AuthProvider>
    </RootErrorBoundary>
  </StrictMode>
);
